import cookies from 'js-cookie';
import { trackGtmEvent } from '@motionelements/core/src/helpers/gtm.js';
import {
    VARIATION_CONTROL,
    SUBSCRIBE_PAGE_DEFAULT_VARIATION_CODE,
    SUBSCRIBE_PAGE_EXPERIMENT_ID,
    experimentCookieName,
    isExperimentControlVariation,
    normalizeVariationCode,
} from '@motionelements/core/src/helpers/experiment.js';

const QUERY_KEY_EXPERIMENT_VARIATION = 'x';
/** Short-lived cookie to prevent reload loops (auto-expires; re-entry after TTL can retry). */
const EXPERIMENT_VARIATION_RELOAD_COOKIE_PREFIX = 'ME_exp_reload_';
const EXPERIMENT_VARIATION_RELOAD_TTL_SECONDS = 15;

function experimentVariationReloadCookieName(experimentId) {
    return `${EXPERIMENT_VARIATION_RELOAD_COOKIE_PREFIX}${experimentId}`;
}

function hasExperimentVariationReloadCookie(experimentId) {
    const value = cookies.get(experimentVariationReloadCookieName(experimentId));
    return value != null && value !== '';
}

function setExperimentVariationReloadCookie(experimentId) {
    cookies.set(experimentVariationReloadCookieName(experimentId), '1', {
        expires: new Date(Date.now() + (EXPERIMENT_VARIATION_RELOAD_TTL_SECONDS * 1000)),
        path: '/',
        sameSite: 'Lax',
    });
}

function clearExperimentVariationReloadCookie(experimentId) {
    cookies.remove(experimentVariationReloadCookieName(experimentId), { path: '/' });
}

export { VARIATION_CONTROL, SUBSCRIBE_PAGE_EXPERIMENT_ID, isExperimentControlVariation };

/** ?x= bucket → tracking variation code (legacy ?x= flow). x=0 = Flagsmith control → no_switcher. */
const EXPERIMENT_X_TO_VARIATION_CODE = {
    [SUBSCRIBE_PAGE_EXPERIMENT_ID]: {
        '0': SUBSCRIBE_PAGE_DEFAULT_VARIATION_CODE,
        '1': 'no_ai',
    },
};

export default {
    data() {
        return {
            experimentVariationCodeById: {},
            experimentReadyById: {},
        };
    },
    computed: {
        /**
         * Edge / AB query ?x= as a string (e.g. '0', '1') — UI bucket only.
         * null when missing. Handles duplicate keys (?x=0&x=1 → first value).
         */
        experimentVariation() {
            if (!this.$route) {
                return null;
            }
            const raw = this.$route.query[QUERY_KEY_EXPERIMENT_VARIATION];
            const value = Array.isArray(raw) ? raw[0] : raw;
            if (value == null || value === '') {
                return null;
            }
            return String(value);
        },
        hasExperimentVariation() {
            // != null: check for null or undefined
            return this.experimentVariation != null;
        },
    },
    methods: {
        /**
         * Variation code from ME_exp_{experimentId} in the browser cookie (set at edge).
         * SSR-safe: returns null on server — use for GTM gating only.
         */
        getVariationCodeFromCookie(experimentId) {
            if (typeof process !== 'undefined' && process.server) {
                return null;
            }
            if (typeof window === 'undefined') {
                return null;
            }
            const value = cookies.get(experimentCookieName(experimentId));
            if (value == null || value === '') {
                return null;
            }
            return normalizeVariationCode(value);
        },
        /**
         * Cookie variation code and/or ?x= present — safe to render (not waiting on edge assignment).
         */
        hasExperimentAssignment(experimentId) {
            if (this.hasExperimentVariation) {
                return true;
            }
            return this.getVariationCodeFromCookie(experimentId) != null;
        },
        isExperimentReady(experimentId) {
            return this.experimentReadyById[experimentId] === true;
        },
        /**
         * Resolved variation code for UI: in-memory state > SSR query (_me_exp) > client cookie.
         */
        resolveVariationCode(experimentId, ssrVariationCode) {
            if (this.experimentVariationCodeById[experimentId] != null && this.experimentVariationCodeById[experimentId] !== '') {
                return normalizeVariationCode(this.experimentVariationCodeById[experimentId]);
            }
            if (ssrVariationCode != null && ssrVariationCode !== '') {
                return normalizeVariationCode(ssrVariationCode);
            }
            const cookieVariationCode = this.getVariationCodeFromCookie(experimentId);
            if (cookieVariationCode != null && cookieVariationCode !== '') {
                return cookieVariationCode;
            }
            return null;
        },
        setVariationCodeState(experimentId, variationCode) {
            this.$set(this.experimentVariationCodeById, experimentId, variationCode);
            this.$set(this.experimentReadyById, experimentId, true);
        },
        setExperimentReadyWithoutAssignment(experimentId) {
            this.$set(this.experimentReadyById, experimentId, true);
        },
        /**
         * Cookie + SSR experiment init (no public ?x=). UI from resolveVariationCode;
         * GTM only when ME_exp_* exists in the browser (worker human cohort).
         */
        initCookieSsrExperiment(experimentId, ssrVariationCode) {
            if (typeof process !== 'undefined' && process.server) {
                return;
            }
            if (this.isExperimentReady(experimentId)) {
                const trackingVariationCode = this.getVariationCodeFromCookie(experimentId);
                if (trackingVariationCode != null && trackingVariationCode !== '') {
                    this.trackExperimentImpression(experimentId, trackingVariationCode);
                }
                return;
            }
            const variationCode = this.resolveVariationCode(experimentId, ssrVariationCode);
            if (variationCode == null || variationCode === '') {
                this.setExperimentReadyWithoutAssignment(experimentId);
                return;
            }
            this.setVariationCodeState(experimentId, variationCode);
            const trackingVariationCode = this.getVariationCodeFromCookie(experimentId);
            if (trackingVariationCode != null && trackingVariationCode !== '') {
                this.trackExperimentImpression(experimentId, trackingVariationCode);
            }
        },
        /**
         * SPA nav skips the edge worker AB redirect — hard-nav so worker can set ?x=.
         * @param {string} experimentId
         * @returns {boolean} true if a reload/navigation was scheduled (caller should abort further work)
         */
        ensureExperimentVariation(experimentId) {
            if (typeof process !== 'undefined' && process.server) {
                return false;
            }
            if (typeof window === 'undefined') {
                return false;
            }
            if (this.hasExperimentVariation) {
                clearExperimentVariationReloadCookie(experimentId);
                return false;
            }
            // Already tried reload within TTL and still no ?x= — soft-fill control so UI can render.
            // Do not track experiment_impression — assignment did not come from the edge.
            if (hasExperimentVariationReloadCookie(experimentId)) {
                clearExperimentVariationReloadCookie(experimentId);
                if (this.$rollbar) {
                    this.$rollbar.warning('experiment variation: infinite reload', {
                        experimentId,
                        route: this.$route && this.$route.fullPath,
                        query: this.$route && this.$route.query,
                    });
                }
                if (this.$router && this.$route) {
                    this.$router.replace({
                        query: {
                            ...this.$route.query,
                            [QUERY_KEY_EXPERIMENT_VARIATION]: '0',
                        },
                    }).catch(() => {});
                }
                return true;
            }
            setExperimentVariationReloadCookie(experimentId);
            // Defer past Vue Router SPA transition — reload() in created() is often cancelled.
            // location.replace(same href) is a no-op; reload hits the edge worker AB 302.
            window.setTimeout(() => {
                window.location.reload();
            }, 0);
            return true;
        },
        /**
         * Edge assignment + GTM impression (legacy ?x= flow).
         */
        initExperiment(experimentId) {
            if (this.ensureExperimentVariation(experimentId)) {
                return;
            }
            this.trackExperimentImpression(experimentId);
        },
        /**
         * Variation code for GTM — legacy ?x= map; cookie SSR passes variation code directly.
         */
        resolveExperimentVariationCode(experimentId) {
            const variationCode = this.experimentVariationCodeById[experimentId];
            if (variationCode != null && variationCode !== '') {
                return variationCode;
            }
            const x = this.experimentVariation;
            if (x == null) {
                return null;
            }
            const map = EXPERIMENT_X_TO_VARIATION_CODE[experimentId];
            if (map) {
                return map[x] ?? null;
            }
            return x; // default: raw ?x=
        },
        /**
         * Push experiment assignment + fire experiment_impression for GTM.
         * @param {string} experimentId
         * @param {string} [variation] optional override for analytics variation code
         */
        trackExperimentImpression(experimentId, variation) {
            if (typeof process !== 'undefined' && process.server) {
                return;
            }
            if (typeof window === 'undefined') {
                return;
            }

            const resolvedVariation = variation != null && variation !== ''
                ? String(variation)
                : this.resolveExperimentVariationCode(experimentId);

            if (resolvedVariation == null || resolvedVariation === '') {
                return;
            }

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                experiment_id: experimentId,
                variation_id: `${experimentId}.${resolvedVariation}`,
            });

            if (this.$gtm) {
                trackGtmEvent(this, {
                    event: 'experiment_impression',
                });
            } else {
                window.dataLayer.push({
                    event: 'experiment_impression',
                });
            }
        },
    },
};
